import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import CustomControl from '../../../../components/global/CustomControl';
import Button from '../../../../components/global/Button';
import ArrowRight from '../../../../components/icons/ArrowRight';
import NavigationBreadcrumbs from '../../../../components/global/NavigationBreadcrumbs';
import { getAlbumDateLabel } from '../../../../util/album';
import {
  selectTargets as selectTargetsAction,
  deselectTargets as deselectTargetsAction,
} from '../../redux/multiTargetSelection/multiTargetSelectionActions';
import useAlbumFolderAlbumsTotal from './util/useAlbumFolderAlbumsTotal';
import AlbumSelectionRow from './components/AlbumSelectionRow';
import AngleLeftIcon from '../../../../components/icons/AngleLeftIcon';

const propTypes = {
  albumFolderTree: PropTypes.object,
  className: PropTypes.string,
  selectedTargets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  deselectTargets: PropTypes.func.isRequired,
  selectTargets: PropTypes.func.isRequired,
  onSelectTarget: PropTypes.func,
  onDeselectTarget: PropTypes.func,
  computeNestedAlbumsTotal: PropTypes.bool,
  albumFolderSelectionEnabled: PropTypes.bool,
  photoGroupSelectionEnabled: PropTypes.bool,
  disabledTargets: PropTypes.arrayOf(PropTypes.string),
  onAlbumFolderOpen: PropTypes.func,
};

const MultiTargetSelection = ({
  albumFolderTree,
  className,
  albumFolderSelectionEnabled = false,
  selectedTargets = [],
  selectTargets,
  deselectTargets,
  onSelectTarget,
  onDeselectTarget,
  computeNestedAlbumsTotal = true,
  photoGroupSelectionEnabled = false,
  disabledTargets = [],
  onAlbumFolderOpen,
}) => {
  const [albumFolderTreePath, setAlbumFolderTreePath] = useState([
    albumFolderTree,
  ]);
  const [expandedAlbumsIds, setExpandedAlbumsIds] = useState([]);

  const currentAlbumFolderTree =
    albumFolderTreePath[albumFolderTreePath.length - 1];

  if (!currentAlbumFolderTree) {
    return null;
  }

  const albumFolderAlbumsTotal = computeNestedAlbumsTotal
    ? useAlbumFolderAlbumsTotal(albumFolderTree)
    : undefined;

  const childAlbumFolders = currentAlbumFolderTree.children.album_folders.records.filter(
    childAlbumFolder => !disabledTargets.includes(childAlbumFolder.id),
  );

  const childAlbums = currentAlbumFolderTree.children.albums.records.filter(
    childAlbum => {
      // Can't upload to video albums from here.
      if (childAlbum.is_video_album) {
        return false;
      }

      if (!photoGroupSelectionEnabled) {
        return !disabledTargets.includes(childAlbum.id);
      }

      const hasPhotoGroups = childAlbum.album_photo_groups.records.length > 0;
      return hasPhotoGroups || !disabledTargets.includes(childAlbum.id);
    },
  );

  const hasTargets = childAlbumFolders.length > 0 || childAlbums.length > 0;

  const onAlbumFolderClick = albumFolder => {
    if (selectedTargets.find(({ id }) => id === albumFolder.id)) {
      deselectTargets([albumFolder]);
      onDeselectTarget?.(albumFolder);
    } else {
      selectTargets([albumFolder]);
      onSelectTarget?.(albumFolder);
    }
  };

  const onAlbumClick = album => {
    // When photo group selection is enabled, we'll display the list of
    // photo groups, and "unlisted" is always an option.
    const hasPhotoGroups = (album.album_photo_groups?.records ?? []).length > 0;

    if (photoGroupSelectionEnabled && hasPhotoGroups) {
      if (expandedAlbumsIds.includes(album.id)) {
        setExpandedAlbumsIds(expandedAlbumsIds.filter(id => album.id !== id));
      } else {
        setExpandedAlbumsIds([...expandedAlbumsIds, album.id]);
      }

      return;
    }

    // Since photo group selection is disabled, we can select the album.
    if (selectedTargets.find(({ id }) => id === album.id)) {
      deselectTargets([album]);
      onDeselectTarget?.(album);
    } else {
      selectTargets([album]);
      onSelectTarget?.(album);
    }
  };

  const onPhotoGroupClick = (album, photoGroup) => {
    const targetData = {
      ...album,
      photoGroup:
        photoGroup.id === album.id
          ? // Avoids copying the entire album object again
            { id: album.id, name: photoGroup.name }
          : photoGroup,
    };

    // Album might be already selected with another photoGroup:
    const selectedAlbumTarget = selectedTargets.find(
      ({ id }) => id === album.id,
    );

    if (selectedAlbumTarget) {
      deselectTargets([album]);
      onDeselectTarget?.(album);
    }

    if (
      !selectedAlbumTarget ||
      selectedAlbumTarget.photoGroup?.id !== photoGroup.id
    ) {
      selectTargets([targetData]);
      onSelectTarget?.(targetData);
    }
  };

  return (
    <div className={className}>
      <div className="my-1">
        <NavigationBreadcrumbs
          trail={albumFolderTreePath.map((albumFolder, index) => ({
            label: albumFolder.name,
            onClick: () => {
              setAlbumFolderTreePath(albumFolderTreePath.slice(0, index + 1));
            },
          }))}
          activeIndex={albumFolderTreePath.length - 1}
          maxCrumbs={6}
        />
      </div>

      {!hasTargets && (
        <tr>
          <td colSpan={4}>No possible targets found.</td>
        </tr>
      )}

      {hasTargets && (
        <div className="table-responsive">
          <table className="table table-striped text-sm">
            <thead>
              <tr>
                <th className="text-left text-bold text-dark">Name</th>
                <th>&nbsp;</th> {/** Photos or Albums. */}
                <th className="text-left text-bold text-dark">Dates</th>
                <th>&nbsp;</th> {/** Actions. */}
              </tr>
            </thead>
            <tbody>
              {albumFolderTreePath.length > 1 && (
                <tr>
                  <td className="text-left" colSpan={4}>
                    <Button
                      btnStyle="link"
                      btnSize="xs"
                      onClick={() => {
                        setAlbumFolderTreePath(
                          albumFolderTreePath.slice(0, -1),
                        );
                      }}
                      className="d-flex align-item-center"
                    >
                      <AngleLeftIcon
                        width={8}
                        color="#0e84fe"
                        className="mr-1"
                      />{' '}
                      Back
                    </Button>
                  </td>
                </tr>
              )}
              {childAlbumFolders.map(albumFolder => {
                return (
                  <tr
                    key={albumFolder.id}
                    onClick={
                      albumFolderSelectionEnabled
                        ? event => {
                            event.preventDefault();
                            onAlbumFolderClick(albumFolder);
                          }
                        : undefined
                    }
                  >
                    <td className="text-left d-flex align-items-center">
                      {albumFolderSelectionEnabled ? (
                        <CustomControl
                          id={albumFolder.id}
                          type="checkbox"
                          checked={
                            !!selectedTargets.find(
                              ({ id }) => id === albumFolder.id,
                            )
                          }
                          readOnly
                          label={albumFolder.name}
                          size="sm"
                        />
                      ) : (
                        <span className="text-sm">{albumFolder.name}</span>
                      )}
                    </td>
                    <td className="text-left">
                      {computeNestedAlbumsTotal && (
                        <Fragment>
                          {albumFolderAlbumsTotal[albumFolder.id]}{' '}
                          {pluralize(
                            'album',
                            albumFolderAlbumsTotal[albumFolder.id],
                          )}
                        </Fragment>
                      )}
                      {!computeNestedAlbumsTotal && (
                        <Fragment>
                          {albumFolder.children.albums.total > 0
                            ? `${albumFolder.children.albums.total} ${pluralize(
                                'album',
                                albumFolder.children.albums.total,
                              )}`
                            : 'N/A'}
                        </Fragment>
                      )}
                    </td>
                    <td className="text-left">
                      {getAlbumDateLabel(albumFolder)}
                    </td>
                    <td>
                      <Button
                        onClick={event => {
                          event.stopPropagation();
                          if (onAlbumFolderOpen) {
                            onAlbumFolderOpen({
                              selectedAlbumFolder: albumFolder,
                              // Passes tree path & setAlbumFolderTreePath
                              // So that external components can totally manage what happens here.
                              albumFolderTreePath,
                              setAlbumFolderTreePath,
                            });
                          } else {
                            setAlbumFolderTreePath([
                              ...albumFolderTreePath,
                              albumFolder,
                            ]);
                          }
                        }}
                        btnStyle="link"
                        data-testid={`${albumFolder.id}-tree-path`}
                      >
                        <ArrowRight size={15} color="#0e84fe" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {childAlbums.map(album => {
                return (
                  <AlbumSelectionRow
                    key={album.id}
                    album={album}
                    isExpanded={expandedAlbumsIds.includes(album.id)}
                    selectedTargets={selectedTargets}
                    photoGroupSelectionEnabled={photoGroupSelectionEnabled}
                    onAlbumClick={() => {
                      onAlbumClick(album);
                    }}
                    onPhotoGroupClick={photoGroup =>
                      onPhotoGroupClick(album, photoGroup)
                    }
                    disabledTargets={disabledTargets}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

MultiTargetSelection.propTypes = propTypes;

const mapStateToProps = ({ pro }) => ({
  selectedTargets: pro.multiTargetSelection.selectedTargets,
});

const mapDispatchToProps = {
  selectTargets: selectTargetsAction,
  deselectTargets: deselectTargetsAction,
};

export { MultiTargetSelection as MultiTargetSelectionComponent };

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  MultiTargetSelection,
);
